import jwtDecode from 'jwt-decode';
import cache from 'util/storage/cache';
import cookie from 'util/storage/cookie';
import {deUrnify} from 'util/network/urnify';
import constants from 'util/data/constants';

const REFRESH_INTERVAL_DURATION = 30 * 60 * 1000; // 30 mins

const AUTH_URL = {
    local: 'http://localhost:8010',
    development: 'https://auth-dev.unearthlabs.com',
    staging: 'https://auth-staging.unearthlabs.com',
    production: 'https://auth.unearthlabs.com'
}[constants.awsProfile];

function parseJson(text) {
    try {
        return JSON.parse(text);
    } catch (e) {
        return text;
    }
}

class AuthManager {
    constructor() {
        this.reset();
    }

    reset() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
        this.credentials = {};
        this.refreshInterval = undefined;
        this.userId = undefined;
    }

    async signIn(credentials = {}) {
        const response = await fetch(`${AUTH_URL}/authenticate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email: credentials.email || credentials.username,
                password: credentials.password
            })
        });
        const creds = parseJson(await response.text());
        if (!creds || !creds.token) {
            throw new Error('Authentication failed');
        }
        this.setCredentials(creds);
        cache.set('env', constants.awsProfile);
        this.refreshOnInterval();
    }

    async reconnect() {
        const refreshToken = cache.get('ue_refresh');
        if (!refreshToken) {
            return false;
        }
        if (this.credentials && this.credentials.token) {
            return true;
        }
        this.setCredentials({refreshToken});
        try {
            await this.refreshToken();
            this.refreshOnInterval();
            return true;
        } catch (e) {
            return false;
        }
    }

    setCredentials(creds) {
        const token = creds.token;
        const refreshToken = creds.refreshToken;
        if (token) {
            this.credentials.token = true;
            document.cookie = `token=${creds.token}; Domain=unearthlabs.com; Secure;`;
            cache.set('ue_token', creds.token);
            const decoded = jwtDecode(token);
            if (decoded && decoded.sub) {
                const urn = deUrnify(decoded.sub);
                if (urn.nid === 'user') {
                    cache.set('user', urn.nss);
                    this.userId = urn.nss;
                }
            }
        }
        if (refreshToken) {
            this.credentials.refreshToken = true;
            cache.set('ue_refresh', creds.refreshToken);
        }
    }

    getToken() {
        return cache.get('ue_token');
    }

    getRefreshToken() {
        return cache.get('ue_refresh');
    }

    async refreshToken() {
        const response = await fetch(`${AUTH_URL}/refresh`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${this.getRefreshToken()}`
            }
        });
        const creds = parseJson(await response.text());
        if (!creds || !creds.token) {
            throw new Error('Token refresh failed');
        }
        this.setCredentials({token: creds.token});
    }

    refreshOnInterval() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
        this.refreshInterval = window.setInterval(() => this.refreshToken(), REFRESH_INTERVAL_DURATION);
    }

    signOut() {
        cookie.removeCookie('messagesUtk');
        cookie.removeCookie('user_id');
        cookie.removeCookie('client_id');
        cookie.removeCookie('token');
        cookie.removeCookie('refreshToken');
        cookie.removeCookie('ue_token');
        cookie.removeCookie('ue_refresh');
        this.reset();
        cache.clear();
    }
}

const authManager = new AuthManager();
export default authManager;
